import React, { useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { Wrapper, ModalTitle, ModalSubtitle, HeaderWrapper, BodyWrapper, ButtonContainer, FormatOptionsWrapper } from "./style";
import { Button } from "../Button";
import CustomRadio from "../RadioInput";
import CustomDatePicker from "../CustomDateRangePicker/CustomDatePicker";
import dayjs from 'dayjs';
import { fileFormatOptions } from './constants';

type Props = {
    exportByFilterFunction: (filterParams: any, clearForm: () => void) => void;
    modalTitle: string;
    modalSubTitle: string;
    noLimit?: boolean;
    submitLabel?: string;
};

const ExportByFilterModal: React.FC<Props> = ({ exportByFilterFunction, modalTitle, modalSubTitle, noLimit = false, submitLabel = "Submit request" }) => {

    const { control, handleSubmit, watch, setValue, formState: { isValid }, reset } = useForm({
        mode: "onChange",
        defaultValues: {
            startDate: "",
            endDate: "",
            transactionType: "",
            fileFormat: "",
        },
    });

    const watchAllFields = watch();

    const handleRadioChange = (name, value) => {
        setValue(name, watchAllFields[name] === value ? "" : value, { shouldValidate: true });
    };

    const today = dayjs().endOf('day');
    const threeMonthsAgo = dayjs().subtract(3, 'month').startOf('day');

    const disableDatesOutsideRange = (current: dayjs.Dayjs) => {
        if (current > today) return true;

        if (!noLimit) {
            return current < threeMonthsAgo;
        }

        return false;
    };

    const clearForm = useCallback(() => {
        reset({
            startDate: "",
            endDate: "",
            transactionType: "",
            fileFormat: "",
        });
    }, [reset]);

    return (
        <Wrapper>
            <HeaderWrapper>
                <ModalTitle>{modalTitle}</ModalTitle>
                <ModalSubtitle>{modalSubTitle}</ModalSubtitle>
            </HeaderWrapper>

            <form onSubmit={handleSubmit((data) => exportByFilterFunction(data, clearForm))}>
                <BodyWrapper>
                    <Controller
                        name="startDate"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <CustomDatePicker
                                value={field.value}
                                onChange={(date) => field.onChange(date)}
                                placeholder=""
                                animatedLabel="Start date"
                                lightBorder={true}
                                disabledDate={disableDatesOutsideRange}
                                defaultPickerValue={!noLimit ? threeMonthsAgo : undefined}
                            />
                        )}
                    />
                    <Controller
                        name="endDate"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <CustomDatePicker
                                value={field.value}
                                onChange={(date) => field.onChange(date)}
                                placeholder=""
                                animatedLabel="End date"
                                lightBorder={true}
                                disabledDate={disableDatesOutsideRange}
                            />
                        )}
                    />

                    <section>
                        <h2>Preferred file format</h2>
                        <div>
                            <Controller
                                name="fileFormat"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <FormatOptionsWrapper>
                                        {fileFormatOptions.map((option) => (
                                            <CustomRadio
                                                key={option.value}
                                                label={option.label}
                                                value={option.value}
                                                onClick={() => handleRadioChange("fileFormat", option.value)}
                                                selected={field.value === option.value}
                                            />
                                        ))}
                                    </FormatOptionsWrapper>
                                )}
                            />
                        </div>
                    </section>
                </BodyWrapper>

                <ButtonContainer>
                    <Button
                        label={submitLabel}
                        type="submit"
                        disabled={!isValid}
                        height="40px"
                        fontSize="12px"
                    />
                </ButtonContainer>
            </form>
        </Wrapper>
    );
};

export default ExportByFilterModal;